import { useMemo, useState } from 'react';
import { createFileRoute, Link, Outlet, useChildMatches, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Archive,
  Loader2,
  MoreVertical,
  Pencil,
  Star,
  Trash2,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { Skeleton } from '@/components/atoms/skeleton';
import { DetailPageLayout, useWorkspaceHeader } from '@/components/layouts';
import { DetailCard } from '@/components/molecules';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useHasCapability } from '@/hooks/usePermissions';
import { apiDelete, apiGet, apiPatch, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerCrew } from '@/types/scheduler';
import { useCrewAppointments } from '@/hooks/scheduler/useCrewAppointments';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { useScheduleTimezone } from '@/lib/time/scheduleTimezone';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';

export const Route = createFileRoute('/_dashboard/dashboard/crews/$id')({
  component: CrewDetailPage,
});

function formatDate(value: string | undefined, timeZone?: string | null) {
  return value
    ? formatDateTimeInTimeZone(value, timeZone, { month: 'short', day: 'numeric', year: 'numeric' })
    : '-';
}

function getInitials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function DetailSkeleton() {
  return (
    <div className="space-y-4 p-5">
      <Skeleton className="h-10 w-48" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

// NOT-626 — `Crew.timezone` is INERT (schema: "the scheduler uses the tenant
// timezone; kept only to avoid a migration"), so it is always the column
// default. Rendering a crew's upcoming visits through it printed New York
// times for every tenant. Schedule times here follow the display contract.
function CrewWhereUsed({ crewId }: { crewId: string }) {
  // Resolved PER VISIT: one crew can hold visits in several site zones, so a
  // page-wide zone would print the out-of-zone ones on company time and drift
  // from the calendar and job pages.
  const scheduleTz = useScheduleTimezone();
  const { data: appointments = [], isLoading } = useCrewAppointments(crewId);
  return (
    <DetailCard label="Where used · upcoming jobs">
      {isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : appointments.length === 0 ? (
        <p className="text-sm text-muted-foreground">No jobs assigned to this crew yet.</p>
      ) : (
        <div className="divide-y">
          {appointments.slice(0, 8).map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{a.jobName ?? a.title}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {a.customerName}
                  {a.startAt
                    ? ` · ${formatDateTimeInTimeZone(
                        a.startAt,
                        scheduleTz.zoneFor(a.siteTimezone),
                        {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        },
                      )}`
                    : ''}
                </p>
              </div>
              <Badge variant="secondary" className="shrink-0 text-[10px]">
                {a.status}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </DetailCard>
  );
}

function KpiCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-semibold">{value}</p>
    </div>
  );
}

function CrewContextHeaderSlot({
  name,
  color,
  isActive,
}: {
  name: string;
  color?: string | null;
  isActive: boolean;
}) {
  const contextHeader = useMemo(
    () => (
      <div className="flex min-w-0 items-center gap-2">
        <span
          className="size-3 shrink-0 rounded-full border"
          style={{ backgroundColor: color ?? 'var(--muted)' }}
        />
        <h2 className="truncate text-base font-semibold">{name}</h2>
        <Badge variant={isActive ? 'default' : 'secondary'}>
          {isActive ? 'Active' : 'Archived'}
        </Badge>
      </div>
    ),
    [name, color, isActive],
  );
  useWorkspaceHeader({ contextHeader }, [contextHeader]);
  return null;
}

function CrewDetailPage() {
  // An audit stamp is a document time (SPEC-tz-0): company zone, not the
  // inert crew stamp.
  const companyTz = useCompanyTimezone();
  const { id } = Route.useParams();
  const router = useRouter();
  const canManageCrew = useHasCapability('can_manage_crew');
  const childMatches = useChildMatches();
  const queryClient = useQueryClient();
  const [showDelete, setShowDelete] = useState(false);
  const crewDetailKey = useMemo(() => [...schedulerKeys.crews(), id] as const, [id]);

  const crewQuery = useApiQuery<SchedulerCrew>({
    queryKey: crewDetailKey,
    queryFn: () => apiGet<SchedulerCrew>(operationsClient, schedulerEndpoints.crew(id)),
    showErrorToast: false,
  });
  const crew = crewQuery.data;

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: schedulerKeys.crews() });
    void queryClient.invalidateQueries({ queryKey: crewDetailKey });
  }

  const deleteMutation = useApiMutation<void, void>({
    mutationFn: () => apiDelete<void>(operationsClient, schedulerEndpoints.crew(id)),
    onSuccess: () => {
      toast.success('Crew deleted');
      setShowDelete(false);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.technicians() });
      void router.navigate({ to: '/dashboard/crews' });
    },
  });

  const archiveMutation = useApiMutation<SchedulerCrew, boolean>({
    mutationFn: (isActive) =>
      apiPatch<SchedulerCrew>(operationsClient, schedulerEndpoints.crew(id), {
        isActive,
      }),
    onSuccess: (_data, isActive) => {
      toast.success(isActive ? 'Crew reactivated' : 'Crew archived');
      invalidate();
    },
  });

  if (childMatches.length > 0) {
    return <Outlet />;
  }

  if (crewQuery.isLoading) return <DetailSkeleton />;

  if (!crew) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-destructive">Crew not found.</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void router.navigate({ to: '/dashboard/crews' })}
        >
          Back to crews
        </Button>
      </div>
    );
  }

  const isActive = crew.isActive !== false;
  const lead = crew.leadTechnicianId
    ? crew.technicians.find((t) => t.id === crew.leadTechnicianId)
    : null;
  const coverage = crew.coverage;
  const members = [...crew.technicians].sort((a, b) => {
    if (a.id === crew.leadTechnicianId) return -1;
    if (b.id === crew.leadTechnicianId) return 1;
    return a.name.localeCompare(b.name);
  });

  // `Crew.timezone` is inert (see CrewWhereUsed) — the scheduler uses the
  // tenant zone. Advertising it here read as "the crew works in this zone",
  // which was never true, so it is not surfaced at all.
  const subtitle = [`${crew.technicians.length}-person team`, lead ? `led by ${lead.name}` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <DetailPageLayout key={id}>
        <CrewContextHeaderSlot name={crew.name} color={crew.color} isActive={isActive} />
        {/* Summary card */}
        <DetailCard className="bg-muted/30">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex min-w-0 items-start gap-2">
              <Button
                variant="ghost"
                size="icon-sm"
                className="-ml-1 shrink-0 md:hidden"
                onClick={() => void router.navigate({ to: '/dashboard/crews' })}
                aria-label="Back to crews"
              >
                <ArrowLeft size={16} />
              </Button>
              <div className="min-w-0 space-y-1">
                <h1 className="truncate text-xl font-bold">{crew.name}</h1>
                <p className="text-sm text-muted-foreground">{subtitle}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {canManageCrew && (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/dashboard/crews/$id/edit" params={{ id: crew.id }}>
                    <Pencil className="mr-1.5 size-3.5" />
                    Edit
                  </Link>
                </Button>
              )}
              {canManageCrew && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={archiveMutation.isPending}
                  onClick={() => archiveMutation.mutate(!isActive)}
                >
                  <Archive className="mr-1.5 size-3.5" />
                  {isActive ? 'Archive' : 'Reactivate'}
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon-sm" aria-label="Crew actions">
                    <MoreVertical size={14} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => setShowDelete(true)}
                    disabled={!canManageCrew}
                  >
                    <Trash2 className="mr-2 size-3.5" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-6 border-t pt-4">
            <div>
              <p className="text-3xl font-bold leading-none">{crew.technicians.length}</p>
              <p className="mt-1 text-xs text-muted-foreground">Members</p>
            </div>
            <KpiCell
              label="Lead"
              value={
                lead ? (
                  <span className="inline-flex items-center gap-1">
                    <Star className="size-3 text-amber-500" />
                    {lead.name}
                  </span>
                ) : (
                  '—'
                )
              }
            />
            <KpiCell label="Skills" value={coverage?.skills.length ?? 0} />
            <KpiCell label="Service areas" value={coverage?.serviceAreas.length ?? 0} />
            <KpiCell label="Capacity" value={coverage ? `${coverage.capacity.perWeek}h/wk` : '—'} />
          </div>
        </DetailCard>

        {/* Members */}
        <DetailCard
          label="Members"
          icon={Users}
          action={
            canManageCrew ? (
              <Button variant="ghost" size="sm" asChild>
                <Link to="/dashboard/crews/$id/edit" params={{ id: crew.id }}>
                  Edit members
                </Link>
              </Button>
            ) : undefined
          }
        >
          {members.length === 0 ? (
            <p className="text-sm text-muted-foreground">No members yet.</p>
          ) : (
            <div className="divide-y">
              {members.map((tech) => {
                const isLead = tech.id === crew.leadTechnicianId;
                return (
                  <div key={tech.id} className="flex items-center gap-3 py-2">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-bold text-muted-foreground">
                      {getInitials(tech.name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{tech.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{tech.email}</p>
                    </div>
                    {isLead ? (
                      <Badge
                        variant="outline"
                        className="border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-400"
                      >
                        <Star className="mr-1 size-3" />
                        Lead
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px]">
                        Helper
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DetailCard>

        {/* Coverage */}
        <DetailCard label="Coverage" description="Rolled up from members (read-only)">
          <div className="space-y-3">
            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Skills</p>
              {coverage && coverage.skills.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {coverage.skills.map((s) => (
                    <Badge key={s.id} variant="secondary" className="text-[11px]">
                      {s.name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No skills.</p>
              )}
            </div>
            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Service areas</p>
              {coverage && coverage.serviceAreas.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {coverage.serviceAreas.slice(0, 30).map((a) => (
                    <Badge key={a.id} variant="outline" className="text-[11px]">
                      {a.zipCode ?? a.city ?? 'Service area unavailable'}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No service areas.</p>
              )}
            </div>
          </div>
        </DetailCard>

        {/* Where used · upcoming jobs */}
        <CrewWhereUsed crewId={crew.id} />

        <p className="px-1 text-xs text-muted-foreground">
          Created {formatDate(crew.createdAt, companyTz)}
        </p>
      </DetailPageLayout>

      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Crew</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{crew.name}</strong>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
